// ---- Bug archetype ----
// The shared many-legged critter anatomy (head + thorax + abdomen, N leg pairs
// splayed out to the sides), parameterised by proportions + palette — so a
// spider / beetle / scorpion-ish crawler is a palette + proportions + a details
// part-list (eyes, mandibles, markings) instead of a from-scratch build.
// Rounded build (plan 28 stage 2): same option surface, limb pivots, and anchor
// formulas as the original boxy build. Same contract as quadruped/humanoid/bird:
// feet at y=0, facing +z, pure meshkit prims, returns named anchor points.

import type { Prims } from "../meshkit.ts";
import { shade } from "../color.ts";
import { ARCHETYPE_ANCHORS, type Vec3 } from "./parts.ts";

/** Proportions + palette for the shared bug body. All sizes optional. */
export type BugOptions = {
  /** Main carapace colour for the thorax. */
  bodyColor: number;
  /** Head colour (default: body colour). */
  headColor?: number;
  /** Leg colour (default: a darker body shade). */
  legColor?: number;
  /** Abdomen colour (default: body colour). */
  abdomenColor?: number;
  /** Leg pairs along the thorax. Default 3 (insect); 4 reads as a spider. */
  legPairs?: number;
  /** Thorax length along the facing axis (+z). Default 0.5. */
  length?: number;
  /** Thorax width across (x). Default 0.5. */
  width?: number;
  /** Thorax height (y). Default 0.3. */
  thickness?: number;
  /** Body clearance above the ground (how tall the legs stand). Default 0.3. */
  stance?: number;
  /** How far each leg reaches sideways past the thorax. Default 0.4. */
  legSpread?: number;
  /** Cube head size. Default 0.26. */
  headSize?: number;
  /** Abdomen blob radius trailing behind the thorax. 0 = none. Default 0.3. */
  abdomen?: number;
};

/** Build the shared bug body into `p` (feet at y=0, facing +z) and return its
 *  anchor points for layering species features (eyes, mandibles, stripes). */
export function bug(p: Prims, o: BugOptions): Record<string, Vec3> {
  const { bone, ball, lathe, limb } = p;
  const len = o.length ?? 0.5;
  const wid = o.width ?? 0.5;
  const thick = o.thickness ?? 0.3;
  const stance = o.stance ?? 0.3;
  const spread = o.legSpread ?? 0.4;
  const pairs = Math.max(1, Math.round(o.legPairs ?? 3));
  const headSz = o.headSize ?? 0.26;
  const abd = o.abdomen ?? 0.3;
  const headCol = o.headColor ?? o.bodyColor;
  const legCol = o.legColor ?? shade(o.bodyColor, -0.25);
  const abdCol = o.abdomenColor ?? o.bodyColor;

  const bodyY = stance + thick / 2;
  const halfLen = len / 2;
  const legR = Math.max(0.02, wid * 0.05);
  const hipY = stance + thick * 0.35; // where the leg arm leaves the body

  // ---- Legs: a femur arching up-out from the flank, a tibia down to ground ----
  // Each leg is a pivot group at the body attach point so the gait animator can
  // scuttle them in an alternating tripod — legL0..legL{n} / legR0..legR{n},
  // indexed front→back. Capsule ends round the knee joint automatically.
  const shinX = wid / 2 + spread;
  for (let i = 0; i < pairs; i++) {
    // Spread pairs along the thorax, front→back (a single pair sits mid-body).
    const t = pairs === 1 ? 0.5 : i / (pairs - 1);
    const z = halfLen * 0.72 - t * len * 0.72;
    for (const sx of [-1, 1]) {
      limb(`${sx < 0 ? "legL" : "legR"}${i}`, [sx * (wid / 2), hipY, z], () => {
        const kneeX = sx * (wid / 2 + spread * 0.6);
        const kneeY = hipY + spread * 0.28;
        bone(sx * (wid / 2 - legR * 0.5), hipY, z, kneeX, kneeY, z, legR * 1.25, legR * 0.85, legCol);
        bone(kneeX, kneeY, z, sx * (shinX + spread * 0.08), legR * 0.45, z, legR * 0.85, legR * 0.5, legCol);
      });
    }
  }

  // ---- Thorax (flattened carapace dome) + head + abdomen ----
  // A lathe covers x/z equally; `depth` stretches z to the authored length.
  const rx = wid / 2;
  lathe(0, stance, 0, [
    [0, 0], [rx * 0.6, thick * 0.08], [rx * 0.92, thick * 0.3], [rx, thick * 0.55],
    [rx * 0.88, thick * 0.82], [rx * 0.5, thick * 0.98], [0, thick * 1.04],
  ], o.bodyColor, len / wid, 10);
  const headZ = halfLen + headSz * 0.42;
  const headY = bodyY + thick * 0.08;
  // Ball slightly larger than the old cube's inscribed sphere so doc-layered
  // face parts (eyes/mandibles at the anchors) still break the surface.
  ball(0, headY, headZ, headSz * 0.55, headCol);
  const abdY = bodyY + abd * 0.18;
  const abdZ = -halfLen - abd * 0.75;
  // Smooth ball, squashed vertically like the old blob (rot z→world y, ×0.78).
  if (abd > 0) ball(0, abdY, abdZ, abd, abdCol, 0.78, [Math.PI / 2, 0, 0]);

  const points: Record<string, Vec3> = {
    root: [0, 0, 0],
    head: [0, headY, headZ],
    face: [0, headY, headZ + headSz / 2],
    crown: [0, headY + headSz / 2, headZ],
    back: [0, stance + thick, 0],
    abdomen: [0, abdY, abdZ],
    rear: [0, abdY, abdZ - abd],
    flank: [wid / 2, bodyY, 0],
  };
  for (const name of ARCHETYPE_ANCHORS.bug) {
    if (!(name in points)) throw new Error(`bug anchor "${name}" missing`);
  }
  return points;
}
